/**
 * Shopping Preferences Context
 *
 * Global context provider for shopping preferences.
 * Provides preferences state and actions to the entire app.
 *
 * Features:
 * - Backend-first sync for authenticated users
 * - Local storage for guest users
 * - Automatic backend synchronization
 * - Optimistic updates
 * - SSR support via cookies
 */

import React, { FCWithChildren, useEffect, useState, useCallback, useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useBucket } from '@context/bucket.context';
import useUser from '@hooks/user/use-user';
import { isBot } from '@helpers/is-bot';
import {
  ShoppingPreferences,
  PreferencesWithoutTimestamp,
  StorageSource,
  ShoppingPreferencesContextValue,
  StorageToClear,
} from '@lib/shopping-preferences/types';
import {
  initializePreferencesV2,
  updatePreferencesWithSyncV2,
  setSessionOnlyPreferencesV2,
  clearPreferencesV2,
  checkUserSelectionV2,
} from '@lib/shopping-preferences-v2/preference-manager-v2';
import { UserShoppingPreferencesService } from '@services/user-shopping-preferences-service';
import { updateHomepageWithPreferences } from '@helpers/shopping-preferences/homepage-preferences-manager';
import logger from '@helpers/utils/logger/client';
import { storage } from '@helpers/shopping-preferences/storage';
const ShoppingPreferencesContext = createContext<ShoppingPreferencesContextValue>(
  {} as ShoppingPreferencesContextValue,
);

interface ShoppingPreferencesProviderProps {
  // Optional: SSR can pass initial preferences
  initialPreferences?: ShoppingPreferences | null;
}

const ShoppingPreferencesProvider: FCWithChildren<ShoppingPreferencesProviderProps> = ({
  children,
  initialPreferences = null,
}) => {
  const { isAuthenticated } = useUser();
  const { isFeatureEnabled } = useBucket();
  const pathname = usePathname();
  const queryClient = useQueryClient();

  // Shopping preferences feature flag
  const isShoppingPreferencesEnabled = isFeatureEnabled((f) => f?.shoppingPreferencesEnableForWeb, true) ?? false;

  // Local state
  const [preferences, setPreferences] = useState<ShoppingPreferences | null>(
    storage.getPermanent() || initialPreferences,
  );
  const [sessionPrefs, setSessionPrefs] = useState<ShoppingPreferences | null>(storage.getTemporary() || null);
  const [source, setSource] = useState<StorageSource>(null);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [hasWindowLoaded, setHasWindowLoaded] = useState(false);
  const [prevAuthenticated, setPrevAuthenticated] = useState<boolean>(isAuthenticated);

  // Track window load state
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (document.readyState === 'complete') {
        setHasWindowLoaded(true);
      } else {
        const onLoad = (): void => setHasWindowLoaded(true);
        window.addEventListener('load', onLoad);
        return () => window.removeEventListener('load', onLoad);
      }
    }
  }, []);

  // Bot detection
  const isUserBot = useMemo(() => {
    return typeof window !== 'undefined' ? isBot(window?.navigator?.userAgent) : false;
  }, [hasWindowLoaded]);

  // Backend query for authenticated users
  const {
    data: backendPrefs,
    isLoading: isBackendLoading,
    isError: isBackendError,
    error: backendError,
    refetch: refetchBackend,
  } = useQuery({
    queryKey: ['shopping-preferences', 'backend'],
    queryFn: async () => {
      logger.info('[Shopping Prefs Context] Fetching from backend');
      const data = await UserShoppingPreferencesService.getShoppingPrefs();
      // If backend has no real universe preference yet, treat as "no prefs"
      // so that v2 sync can handle defaults / temp correctly without
      // fabricating a permanent universe = 1 with a fresh timestamp.
      if (!data?.universe) {
        return null;
      }

      return {
        universe: data.universe,
        categories: data.categories,
        custom: data.custom,
        // Only generate a new timestamp when backend actually has a universe value.
        // This avoids giving "fake" freshness to fallback values.
        last_updated: data.updated_at || new Date().toISOString(),
      } as ShoppingPreferences;
    },
    enabled: isShoppingPreferencesEnabled && isAuthenticated && hasWindowLoaded,
    staleTime: Infinity,
    retry: 2,
    onSuccess: (data) => {
      logger.info('[Shopping Prefs Context] Backend preferences fetched successfully', data);
    },
  });

  // Backend mutation for updates
  const updateBackendMutation = useMutation({
    mutationFn: async (prefs: PreferencesWithoutTimestamp) => {
      logger.info('[Shopping Prefs Context] Syncing to backend');
      await UserShoppingPreferencesService.updateShoppingPrefs({
        universe: prefs.universe,
        categories: prefs.categories,
        custom: prefs.custom,
      });
    },
    onSuccess: () => {
      logger.info('[Shopping Prefs Context] Backend sync successful');
      queryClient.invalidateQueries({ queryKey: ['shopping-preferences', 'backend'] });
    },
    onError: (error) => {
      logger.error('[Shopping Prefs Context] Backend sync failed:', error);
    },
    retry: 2,
  });

  // Initialize preferences on mount (client-side hydration)
  useEffect(() => {
    // Skip hydration for bots & mark as hydrated immediately to prevent infinite loading states
    if (isUserBot) {
      setHasHydrated(true);
      return;
    }

    if (!hasWindowLoaded && hasHydrated) {
      return;
    }

    if (!isShoppingPreferencesEnabled && hasHydrated) {
      return;
    }

    logger.info('[Shopping Prefs Context] Hydrating preferences from storage');

    // For authenticated users, wait for backend query
    if (isAuthenticated && !backendPrefs) {
      return;
    }

    // Detect if user just logged in (auth state changed from false to true)
    const justLoggedIn = !prevAuthenticated && isAuthenticated;
    if (justLoggedIn) {
      logger.info('[Shopping Prefs Context] User just logged in, delegating temporary storage clear to v2 sync');
    }

    // Initialize from storage + backend (if authenticated), with v2 sync strategy
    // Clear temporary storage if user just logged in to prevent conflicts with backend preferences, as per HP behavior
    const syncResult = initializePreferencesV2(backendPrefs || null, isAuthenticated, justLoggedIn);

    if (syncResult.success) {
      setPreferences(syncResult.preferences);
      setSessionPrefs(syncResult.sessionPrefs);
      setSource(syncResult.source);

      // Update homepage if backend has prefs
      if (!prevAuthenticated && isAuthenticated && syncResult.source === 'backend' && pathname === '/') {
        updateHomepageWithPreferences(syncResult.preferences);
      }
    }

    setPrevAuthenticated(isAuthenticated);
    setHasHydrated(true);
  }, [
    isShoppingPreferencesEnabled,
    hasWindowLoaded,
    isAuthenticated,
    backendPrefs,
    hasHydrated,
    pathname,
    prevAuthenticated,
    isUserBot,
  ]);

  // Update preferences (optimistic update with backend sync)
  const updatePreferences = useCallback(
    async (newPrefs: PreferencesWithoutTimestamp): Promise<void> => {
      if (!isShoppingPreferencesEnabled) {
        logger.warn('[Shopping Prefs Context] Feature disabled, cannot update');
        return;
      }

      try {
        // The new preferences that get into state come from result.preferences, not from any closed-over `preferences` variable.
        const result = updatePreferencesWithSyncV2(newPrefs, isAuthenticated);
        // Optimistic update
        setPreferences(result.preferences);
        setSource(isAuthenticated ? 'backend' : 'cookie');

        // Update homepage if needed
        updateHomepageWithPreferences(result.preferences);

        // Sync to backend if authenticated
        if (result.shouldSyncBackend) {
          await updateBackendMutation.mutateAsync(newPrefs);
        }

        logger.info('[Shopping Prefs Context] Preferences updated successfully');
      } catch (error) {
        logger.error('[Shopping Prefs Context] Failed to update preferences:', error);
        throw error;
      }
    },
    [isAuthenticated, isShoppingPreferencesEnabled, updateBackendMutation],
  );

  // Set session preferences (temporary, no backend sync)
  const setSessionPreferences = useCallback(
    (newPrefs: PreferencesWithoutTimestamp): void => {
      if (!isShoppingPreferencesEnabled) {
        return;
      }

      try {
        const updated = setSessionOnlyPreferencesV2(newPrefs);
        setSessionPrefs(updated);
        setSource('cookie');

        logger.info('[Shopping Prefs Context] Session preferences set');
      } catch (error) {
        logger.error('[Shopping Prefs Context] Failed to set session preferences:', error);
      }
    },
    [isShoppingPreferencesEnabled],
  );

  // Clear preferences
  const clearPreferences = useCallback(
    (storage: StorageToClear): void => {
      if (!isShoppingPreferencesEnabled) {
        return;
      }

      try {
        const defaults = clearPreferencesV2(storage);

        if (storage === 'all' || storage === 'permanent') {
          setPreferences(defaults);
          setSource('default');
        }

        if (storage === 'all' || storage === 'temporary') {
          setSessionPrefs(null);
        }

        logger.info('[Shopping Prefs Context] Preferences cleared');
      } catch (error) {
        logger.error('[Shopping Prefs Context] Failed to clear preferences:', error);
      }
    },
    [isShoppingPreferencesEnabled],
  );

  // Refetch from backend
  const refetch = useCallback(async (): Promise<void> => {
    if (isAuthenticated) {
      await refetchBackend();
    }
  }, [isAuthenticated, refetchBackend]);

  // Check if user has selected preferences
  const hasUserSelectedPrefs = useMemo(() => {
    return checkUserSelectionV2(preferences);
  }, [preferences]);

  // Loading state
  const isLoading = useMemo(() => {
    if (!isShoppingPreferencesEnabled) {
      return false;
    }

    // Still loading if authenticated and waiting for backend
    if (isAuthenticated && !hasHydrated) {
      return isBackendLoading;
    }

    return !hasHydrated;
  }, [isShoppingPreferencesEnabled, isAuthenticated, hasHydrated, isBackendLoading]);

  // Context value
  const value: ShoppingPreferencesContextValue = useMemo(
    () => ({
      preferences,
      sessionPrefs,
      source,
      isLoading,
      isError: isBackendError,
      error: backendError as Error | null,
      isEnabled: isShoppingPreferencesEnabled,
      hasUserSelectedPrefs,
      updatePreferences,
      setSessionPreferences,
      clearPreferences,
      refetch,
    }),
    [
      preferences,
      sessionPrefs,
      source,
      isLoading,
      isBackendError,
      backendError,
      isShoppingPreferencesEnabled,
      hasUserSelectedPrefs,
      updatePreferences,
      setSessionPreferences,
      clearPreferences,
      refetch,
    ],
  );

  // Always render Provider to prevent runtime errors
  return <ShoppingPreferencesContext.Provider value={value}>{children}</ShoppingPreferencesContext.Provider>;
};

export { ShoppingPreferencesProvider, ShoppingPreferencesContext };
